import { Link } from "@/lib/link";
import { ArrowRight, Compass, ShoppingBag, Truck, ShieldCheck, Store, Rocket } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import ScrollReveal from "@/components/common/ScrollReveal";
import SectionHeading from "@/components/common/SectionHeading";
import { Button } from "@/components/ui/button";

const audience = [
    "importBusiness.audience.gcc",
    "importBusiness.audience.freelancers",
    "importBusiness.audience.corporate",
    "importBusiness.audience.marketplaces",
    "importBusiness.audience.tradingCompanies",
];

const steps = [
    { Icon: Compass, tKey: "importBusiness.steps.productSelection.title", dKey: "importBusiness.steps.productSelection.description" },
    { Icon: ShoppingBag, tKey: "importBusiness.steps.supplierSelection.title", dKey: "importBusiness.steps.supplierSelection.description" },
    { Icon: ShieldCheck, tKey: "importBusiness.steps.sampleQc.title", dKey: "importBusiness.steps.sampleQc.description" },
    { Icon: Truck, tKey: "importBusiness.steps.shipping.title", dKey: "importBusiness.steps.shipping.description" },
    { Icon: Store, tKey: "importBusiness.steps.branding.title", dKey: "importBusiness.steps.branding.description" },
    { Icon: Rocket, tKey: "importBusiness.steps.support.title", dKey: "importBusiness.steps.support.description" },
];

export default function ImportBusiness() {
    const { t } = useTranslation();
    return (
        <>
            <SEO
                title="Start Your Import Business — For NRIs, Diaspora & Amazon Sellers | Exportfy.world"
                description="Want to start importing Indian products into your country? Exportfy becomes your trusted sourcing and export partner — from product selection to shipping, branding, and repeat orders."
                path="/import-business"
            />
            <PageHero
                eyebrow={t("importBusiness.hero.eyebrow")}
                title={t("importBusiness.hero.title")}
                subtitle={t("importBusiness.hero.subtitle")}
                image="https://images.unsplash.com/photo-1553413077-190dd305871c?w=1600"
            >
                <Link to="/consultation"><Button className="rounded-full bg-white text-[#0A2540] hover:bg-slate-100 font-semibold px-7 py-6">{t("importBusiness.hero.button")} <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            </PageHero>

            <section className="section-y">
                <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-14">
                    <ScrollReveal className="lg:col-span-1">
                        <div className="overline mb-3">{t("importBusiness.audience.eyebrow")}</div>
                        <h2 className="text-3xl lg:text-4xl font-extrabold text-[#0A2540] tracking-tight leading-tight">{t("importBusiness.audience.title")}</h2>
                    </ScrollReveal>
                    <ScrollReveal className="lg:col-span-2" delay={80}>
                        <ul className="space-y-4">
                            {audience.map((a) => (
                                <li key={a} className="flex items-start gap-3 text-slate-700 text-lg">
                                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">✓</span>
                                    {t(a)}
                                </li>
                            ))}
                        </ul>
                    </ScrollReveal>
                </div>
            </section>

            <section className="section-y bg-[#F8FAFC]">
                <div className="container-x">
                    <ScrollReveal><SectionHeading eyebrow={t("importBusiness.steps.eyebrow")} title={t("importBusiness.steps.title")} /></ScrollReveal>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {steps.map(({ Icon, tKey, dKey }, i) => (
                            <ScrollReveal key={tKey} delay={i * 50}>
                                <div className="bg-white border border-slate-200 rounded-2xl p-7 h-full hover:shadow-[0_20px_40px_rgba(10,37,64,0.08)] hover:-translate-y-0.5 transition-all">
                                    <div className="w-11 h-11 rounded-xl bg-[#1D4ED8] flex items-center justify-center text-white"><Icon className="w-5 h-5" strokeWidth={1.75} /></div>
                                    <div className="mt-5 font-bold text-lg text-[#0A2540]">{t(tKey)}</div>
                                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t(dKey)}</p>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section-y bg-[#0A2540] text-white">
                <div className="container-x flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                    <div>
                        <div className="overline text-emerald-400">{t("importBusiness.cta.eyebrow")}</div>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">{t("importBusiness.cta.title")}</h2>
                        <p className="mt-3 text-white/70 max-w-2xl">{t("importBusiness.cta.description")}</p>
                    </div>
                    <Link to="/consultation"><Button className="rounded-full bg-white text-[#0A2540] hover:bg-slate-100 font-semibold px-7 py-6">{t("importBusiness.cta.button")}</Button></Link>
                </div>
            </section>
        </>
    );
}
